<script setup>
// First-login welcome. Offers the product tour or "explore on my own".
// Shows once per user (persisted in UI settings).
import { ref, onMounted } from 'vue';
import { useMesselloHelp } from 'dashboard/composables/useMesselloHelp';
import { useUISettings } from 'dashboard/composables/useUISettings';

const { startTour } = useMesselloHelp();
const { uiSettings, updateUISettings } = useUISettings();

const visible = ref(false);

// Give UI settings a moment to load, then show once for new users.
onMounted(() => {
  setTimeout(() => {
    const s = uiSettings.value || {};
    if (!s.messello_welcome_seen && !s.messello_tour_done) {
      visible.value = true;
    }
  }, 1400);
});

const seen = () => {
  visible.value = false;
  updateUISettings({ messello_welcome_seen: true });
};
const takeTour = () => {
  seen();
  startTour();
};
</script>

<template>
  <div v-if="visible" class="mz-welcome">
    <div class="mz-welcome-scrim" @click="seen" />
    <div class="mz-welcome-card" role="dialog" aria-label="Welcome to Messello">
      <div class="mz-welcome-mark">
        <svg width="52" height="52" viewBox="0 0 96 96">
          <rect width="96" height="96" rx="22" fill="#3B6CF6" />
          <g stroke="#fff" stroke-width="5.5" stroke-linecap="round">
            <line x1="48" y1="49" x2="48" y2="26" />
            <line x1="48" y1="49" x2="69" y2="63" />
            <line x1="48" y1="49" x2="27" y2="63" />
          </g>
          <rect x="39" y="40" width="18" height="18" rx="5" fill="#fff" />
          <circle cx="48" cy="26" r="8.5" fill="#2DD4BF" />
          <circle cx="69" cy="63" r="8.5" fill="#FBBF24" />
          <circle cx="27" cy="63" r="8.5" fill="#E0E7FF" />
        </svg>
      </div>
      <h2 class="mz-welcome-h">Welcome to Messello 👋</h2>
      <p class="mz-welcome-p">
        Your all-in-one shared inbox is ready. Take a quick 60-second tour to see
        how to connect a channel, turn on AI, and invite your team — or explore on
        your own. You can reopen the tour anytime from the ? button.
      </p>
      <div class="mz-welcome-btns">
        <button class="mz-w-primary" @click="takeTour">Take the 60-sec tour</button>
        <button class="mz-w-secondary" @click="seen">Explore on my own</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mz-welcome {
  position: fixed;
  inset: 0;
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.mz-welcome-scrim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
}
.mz-welcome-card {
  position: relative;
  width: 440px;
  max-width: 92vw;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.3);
  padding: 32px 30px 26px;
  text-align: center;
}
.mz-welcome-mark {
  margin-bottom: 14px;
}
.mz-welcome-h {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #0f172a;
  margin: 0 0 10px;
}
.mz-welcome-p {
  font-size: 15px;
  line-height: 1.55;
  color: #475569;
  margin: 0 0 22px;
}
.mz-welcome-btns {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mz-w-primary {
  border: 0;
  background: #3b6cf6;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  padding: 13px;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
}
.mz-w-primary:hover {
  background: #2a55d6;
}
.mz-w-secondary {
  border: 1px solid #e6eaf1;
  background: #fff;
  color: #334155;
  font-size: 15px;
  font-weight: 600;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
}
.mz-w-secondary:hover {
  border-color: #cdd7ea;
}
</style>
